import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

/**
 * Per ADR-0073. Holds the engagement token + tenantId + projectId for a
 * visitor who arrived from the marketing site (`apps/site` hero) and is
 * driving the Creator without a real account.
 *
 * Persisted to **sessionStorage** (per Andrey 2026-05-16):
 *  - tab-scoped (does NOT leak between tabs/windows, unlike localStorage);
 *  - survives a single-tab page reload — without this, hard-refreshing
 *    the GoalsDialog page dropped the engagement token and the SPA
 *    treated the visitor as authenticated, hiding EngagePanel + ClaimAgentModal
 *    and showing the wrong call surface;
 *  - the engagement token is short-lived (2h TTL signed by the server),
 *    so worst-case session-storage leak is bounded by that.
 *
 * URL hydration still wins on first arrival (apps/site → apps/web with
 * `?anon=1&t=…`), but on subsequent reloads the sessionStorage copy
 * keeps the store alive after OnboardingEntryPage has stripped the
 * query string.
 *
 * Hydration order (see `main.ts`):
 *   1. `useAuthStore().restoreFromStorage()`  — durable bearer/devShim
 *   2. `hydrateAnonymousSession()`             — read URL OR sessionStorage
 *   3. Router runs guards
 *
 * The auth-store hydration runs first because an *authenticated* visitor
 * landing on `/onboarding?anon=1` should still be able to log in normally
 * — the modal in OnboardingEntryPage gives them the explicit choice
 * between using their tenant and continuing anonymously.
 */

const SESSION_STORAGE_KEY = 'aitelier:anon-session/v1';

interface PersistedShape {
  engagementToken: string;
  tenantId: string;
  projectId: string;
  locale?: string | undefined;
}

function readPersisted(): PersistedShape | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedShape>;
    if (
      typeof parsed.engagementToken === 'string' &&
      typeof parsed.tenantId === 'string' &&
      typeof parsed.projectId === 'string'
    ) {
      return {
        engagementToken: parsed.engagementToken,
        tenantId: parsed.tenantId,
        projectId: parsed.projectId,
        locale: typeof parsed.locale === 'string' ? parsed.locale : undefined,
      };
    }
    return null;
  } catch {
    return null;
  }
}

function writePersisted(value: PersistedShape | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(value));
    }
  } catch {
    // sessionStorage may be disabled (privacy mode, quota). The store
    // still works in-memory; reload will lose state — same as before.
  }
}

export const useAnonymousSessionStore = defineStore('anonymousSession', () => {
  const engagementToken = ref<string | undefined>();
  const tenantId = ref<string | undefined>();
  const projectId = ref<string | undefined>();
  /** Locale the visitor was on when they submitted the hero (en | ru | es).
   *  Stored so apps/web can switch i18n to match without a second sniff. */
  const locale = ref<string | undefined>();

  const isActive = computed(
    () => Boolean(engagementToken.value && tenantId.value && projectId.value),
  );

  function setSession(input: {
    engagementToken: string;
    tenantId: string;
    projectId: string;
    locale?: string | undefined;
  }): void {
    engagementToken.value = input.engagementToken;
    tenantId.value = input.tenantId;
    projectId.value = input.projectId;
    locale.value = input.locale;
  }

  function clear(): void {
    engagementToken.value = undefined;
    tenantId.value = undefined;
    projectId.value = undefined;
    locale.value = undefined;
  }

  // Mirror every state mutation to sessionStorage. `flush: 'sync'` so a
  // page-unload immediately after setSession (e.g. router.replace +
  // immediate reload) doesn't lose the just-written value.
  watch(
    [engagementToken, tenantId, projectId, locale],
    () => {
      if (engagementToken.value && tenantId.value && projectId.value) {
        writePersisted({
          engagementToken: engagementToken.value,
          tenantId: tenantId.value,
          projectId: projectId.value,
          locale: locale.value,
        });
      } else {
        writePersisted(null);
      }
    },
    { flush: 'sync' },
  );

  return { engagementToken, tenantId, projectId, locale, isActive, setSession, clear };
});

/**
 * Reads `?anon=1&t=<token>&tenant=<id>&p=<projectId>&locale=<en|ru|es>`
 * from the current URL OR sessionStorage and seeds the store if present.
 * Called once at boot from `main.ts`.
 *
 * URL wins over sessionStorage so re-clicking the marketing hero from
 * another agent path updates the session to the new project.
 */
export function hydrateAnonymousSessionFromUrl(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('anon') === '1' ? params : null;
  const store = useAnonymousSessionStore();
  if (fromUrl) {
    const t = fromUrl.get('t');
    const tenant = fromUrl.get('tenant');
    const p = fromUrl.get('p');
    if (t && tenant && p) {
      store.setSession({
        engagementToken: t,
        tenantId: tenant,
        projectId: p,
        locale: fromUrl.get('locale') ?? undefined,
      });
      return;
    }
  }
  const persisted = readPersisted();
  if (persisted) {
    store.setSession(persisted);
  }
}
